import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, ModalBody, ModalFooter } from 'shards-react';
import ModalEditHeader from '../../common/ModalEditHeader';
import TableReview from './TableReview';
import FormReason from './FormReason';
import useLocales from '../../../hooks/useLocales';
import { RootState, useDispatch, useSelector } from '../../../redux/store';
import { closeDialog, showErrorMessage, updateCondition } from '../../../redux/slices/approval';
import getOrganizationDisplayName from '../../../utils/getOrganizationDisplayName';
import moment from 'moment';
import type { UpdateConditionParams } from '../../../types/condition';
import type { UserType } from '../../../types/user';
import type { OrganizationType } from '../../../types/organization';

const ModalManageApproval = (): React.ReactElement => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { currentLang } = useLocales();
  const { organizations } = useSelector((state: RootState) => state.organization);
  const { adminPowerUsers } = useSelector((state: RootState) => state.user);
  const { isDialogOpen, confirmCondition } = useSelector((state: RootState) => state.approval);
  const { steelMaker, businessType, steelCategory, organizationBase } = useSelector((state: RootState) => state.options);
  const [organizationName, setOrganizationName] = useState<string>('');
  const [approverName, setApproverName] = useState<string>('');
  const [applicantName, setApplicantName] = useState<string>('');
  const [reason, setReason] = useState<string>('');

  // SIDE EFFECT
  // ================================================================================

  /**
   * 確認用
   */
  //  useEffect(() => {
  //   console.log(reason);
  // }, [reason]);

  /**
   * 組織名設定
   */
  useEffect(() => {
    if (confirmCondition) {
      const organization: OrganizationType | undefined = organizations.find((organization) => organization.id === confirmCondition.organizationId);
      const name: string = organization ? getOrganizationDisplayName(organization, { organizationBases: organizationBase, language: currentLang.value }) : '';
      setOrganizationName(name);
    }
  }, [confirmCondition]);

  /**
   * 依頼者のName設定
   */
  useEffect(() => {
    if (confirmCondition && adminPowerUsers) {
      // 依頼者はadminかもしれないしpoweruserかもしれない
      const applicantAsAdmin: UserType | undefined = adminPowerUsers.admin.find((admin) => admin.id === confirmCondition.applicantId);
      const applicantAsPoweruser: UserType | undefined = adminPowerUsers.poweruser.find((poweruser) => poweruser.id === confirmCondition.applicantId);
      const applicant: UserType | undefined = applicantAsAdmin ? applicantAsAdmin : applicantAsPoweruser;
      setApplicantName(applicant ? applicant.username : confirmCondition.applicantId);
    }
  }, [confirmCondition, adminPowerUsers]);

  /**
   * 承認者のName設定
   */
  useEffect(() => {
    if (confirmCondition && adminPowerUsers) {
      const approver: UserType | undefined = adminPowerUsers.admin.find((admin) => admin.id === confirmCondition.approverId);
      setApproverName(approver ? approver.username : confirmCondition.approverId);
    }
  }, [confirmCondition, adminPowerUsers]);

  // EVENT HANDLER
  // ================================================================================

  /**
   * 差し戻し理由を変更した時
   */
  const handleReasonChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    // console.log(event);
    const newValue: string = event.target.value;
    setReason(newValue);
  };

  /**
   * モーダルでキャンセルボタンを押した時
   */
  const handleCancel = (): void => {
    dispatch(closeDialog());
    setReason('');
  };

  /**
   * モーダルで差し戻しボタンを押した時
   */
  const handleReject = (): void => {
    if (confirmCondition) {
      const params: UpdateConditionParams = {
        conditionStatus: 'EDITING',
        applicantId: '',
        rejectReason: reason,
        judgmentDate: moment().utc().format().toString(),
      };
      dispatch(updateCondition(confirmCondition.id, params));
    } else {
      dispatch(showErrorMessage('対象の可視制御が選択されていません'));
    }
    dispatch(closeDialog());
    setReason('');
  };

  /**
   * モーダルで承認ボタンを押した時
   */
  const handleSave = (): void => {
    if (confirmCondition) {
      const params: UpdateConditionParams = {
        conditionStatus: 'APPROVED',
        judgmentDate: moment().utc().format().toString(),
      };
      dispatch(updateCondition(confirmCondition.id, params));
    } else {
      dispatch(showErrorMessage('対象の可視制御が選択されていません'));
    }
    dispatch(closeDialog());
    setReason('');
  };

  // VIEW
  // ================================================================================

  return (
    <Modal id="modalManageApproval" className="modal-dialog-scrollable" open={isDialogOpen} maxWidth={'lg'} size={'lg'} fullWidth={true} centered={true} toggle={handleCancel}>
      <ModalEditHeader iconName={'checklist'} title={t('approval.conditionApproval')} />
      <ModalBody>
        {confirmCondition ? (
          <>
            <TableReview
              organizationName={organizationName}
              applicantName={applicantName}
              approverName={approverName}
              confirmCondition={confirmCondition}
              steelMaker={steelMaker}
              businessType={businessType}
              steelCategory={steelCategory}
              language={currentLang.value}
            />
            <FormReason title={t('approval.reason')} onChange={handleReasonChange} />
          </>
        ) : (
          <div className="text-center">
            <span className="spinner-border text-primary"></span>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button theme="light" onClick={handleCancel}>
          {t('dialog.cancel')}
        </Button>
        <Button theme="primary" onClick={handleReject}>
          {t('sendBack')}
        </Button>
        <Button theme="primary" onClick={handleSave}>
          {t('approval.approval')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ModalManageApproval;
